import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import type {
    InsertVesselDashboardAccessRequest,
    VesselDashboardAccessItem,
} from '@routes/sidebar/admin-center/tenant-client/___pages___/roles/api/vesselDashboardAccess.api.ts';
import {
    createApiVesselDashboardAccess,
    deleteApiVesselDashboardAccess,
    getApiVesselDashboardAccess,
    updateApiVesselDashboardAccess,
} from '@routes/sidebar/admin-center/tenant-client/___pages___/roles/api/vesselDashboardAccess.api.ts';
import { extractError } from '@utils/slice.ts';

interface VesselDashboardAccessError {
    message?: string;
    [k: string]: any;
}

interface VesselDashboardAccessState {
    error: VesselDashboardAccessError | null;

    vesselDashboardAccessList: VesselDashboardAccessItem[];
    vesselDashboardAccessItem: VesselDashboardAccessItem | null;

    isGetVesselDashboardAccessPending: boolean;
    isCreateVesselDashboardAccessPending: boolean;
    isUpdateVesselDashboardAccessPending: boolean;
    isDeleteVesselDashboardAccessPending: boolean;
}

const initialState: VesselDashboardAccessState = {
    error: null,

    vesselDashboardAccessList: [],
    vesselDashboardAccessItem: null,

    isGetVesselDashboardAccessPending: false,
    isCreateVesselDashboardAccessPending: false,
    isUpdateVesselDashboardAccessPending: false,
    isDeleteVesselDashboardAccessPending: false,
};

export const getVesselDashboardAccess = createAsyncThunk<
    VesselDashboardAccessItem[],
    { tenantAlias: string },
    { rejectValue: VesselDashboardAccessError }
>('vesselDashboardAccess/getVesselDashboardAccess', ({ tenantAlias }, { rejectWithValue }) =>
    getApiVesselDashboardAccess(tenantAlias)
        .then((res) => res)
        .catch((err) => rejectWithValue(err)),
);

export const createVesselDashboardAccess = createAsyncThunk<
    VesselDashboardAccessItem,
    { tenantAlias: string; payload: InsertVesselDashboardAccessRequest },
    { rejectValue: VesselDashboardAccessError }
>('vesselDashboardAccess/createVesselDashboardAccess', ({ tenantAlias, payload }, { rejectWithValue }) =>
    createApiVesselDashboardAccess(tenantAlias, payload)
        .then((res) => res)
        .catch((err) => rejectWithValue(err)),
);

export const updateVesselDashboardAccess = createAsyncThunk<
    VesselDashboardAccessItem,
    { tenantAlias: string; accessId: number; payload: InsertVesselDashboardAccessRequest },
    { rejectValue: VesselDashboardAccessError }
>('vesselDashboardAccess/updateVesselDashboardAccess', ({ tenantAlias, accessId, payload }, { rejectWithValue }) =>
    updateApiVesselDashboardAccess(tenantAlias, accessId, payload)
        .then((res) => res)
        .catch((err) => rejectWithValue(err)),
);

export const deleteVesselDashboardAccess = createAsyncThunk<
    void,
    { tenantAlias: string; accessId: number },
    { rejectValue: VesselDashboardAccessError }
>('vesselDashboardAccess/deleteVesselDashboardAccess', ({ tenantAlias, accessId }, { rejectWithValue }) =>
    deleteApiVesselDashboardAccess(tenantAlias, accessId)
        .then(() => undefined)
        .catch((err) => rejectWithValue(err)),
);

const vesselDashboardAccessSlice = createSlice({
    name: 'vesselDashboardAccess',
    initialState,
    reducers: {},
    extraReducers: (builder) => {
        builder.addCase(getVesselDashboardAccess.pending, (state) => {
            state.isGetVesselDashboardAccessPending = true;
            state.error = null;
        });
        builder.addCase(getVesselDashboardAccess.fulfilled, (state, action) => {
            state.isGetVesselDashboardAccessPending = false;
            state.vesselDashboardAccessList = action.payload;
        });
        builder.addCase(getVesselDashboardAccess.rejected, (state, action) => {
            state.isGetVesselDashboardAccessPending = false;
            state.error = extractError(action);
        });

        builder.addCase(createVesselDashboardAccess.pending, (state) => {
            state.isCreateVesselDashboardAccessPending = true;
            state.error = null;
        });
        builder.addCase(createVesselDashboardAccess.fulfilled, (state, action) => {
            state.isCreateVesselDashboardAccessPending = false;
            state.vesselDashboardAccessItem = action.payload;
        });
        builder.addCase(createVesselDashboardAccess.rejected, (state, action) => {
            state.isCreateVesselDashboardAccessPending = false;
            state.error = extractError(action);
        });

        builder.addCase(updateVesselDashboardAccess.pending, (state) => {
            state.isUpdateVesselDashboardAccessPending = true;
            state.error = null;
        });
        builder.addCase(updateVesselDashboardAccess.fulfilled, (state, action) => {
            state.isUpdateVesselDashboardAccessPending = false;
            state.vesselDashboardAccessItem = action.payload;
        });
        builder.addCase(updateVesselDashboardAccess.rejected, (state, action) => {
            state.isUpdateVesselDashboardAccessPending = false;
            state.error = extractError(action);
        });

        builder.addCase(deleteVesselDashboardAccess.pending, (state) => {
            state.isDeleteVesselDashboardAccessPending = true;
            state.error = null;
        });
        builder.addCase(deleteVesselDashboardAccess.fulfilled, (state) => {
            state.isDeleteVesselDashboardAccessPending = false;
        });
        builder.addCase(deleteVesselDashboardAccess.rejected, (state, action) => {
            state.isDeleteVesselDashboardAccessPending = false;
            state.error = extractError(action);
        });
    },
});

export default vesselDashboardAccessSlice.reducer;
